import { useRouter, useSearch } from '@tanstack/react-router';
import { Download, TriangleAlert } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { EVENT_LOG_COUNT_CAP, EVENT_LOG_NEWER_CAP, EVENT_LOG_PAGE_SIZE, type EventLogRow } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Pager } from '../../../components/Pager';
import { EmptyState, FilteredEmptyState, ForbiddenState, SystemState } from '../../../components/system-states/SystemState';
import { errorMessage } from '../../../lib/api-client';
import { formatNumber } from '../../../lib/format';
import { useMediaQuery } from '../../../lib/use-media-query';
import { useCanView } from '../../../stores/session';
import { useEventLog, useEventLogExport, useEventLogFacets, useEventLogNewer, useEventLogStory, useShowNewer } from '../api/event-log-api';
import { eventLogHref, filterCount, filtersOf, listSearchOf, readEventLogSearch, withFilters, type EventLogSearch } from '../components/event-log/event-log-address';
import { EventLogDetail } from '../components/event-log/EventLogDetail';
import { EventLogFilters } from '../components/event-log/EventLogFilters';
import { EventLogStory } from '../components/event-log/EventLogStory';
import { EventLogTable } from '../components/event-log/EventLogTable';
import styles from './EventLogPage.module.css';

/**
 * Admin · Event log (S7's inspector; Admin redesign 2026-11-02, Option A of `6-logs/event-log.html`): the filters as
 * pills, a full-width table of plain sentences with the record each is about, and the clicked event's before and
 * after in a pane beside it; "Everything that happened to this record" opens one record's story.
 *
 * `events` is the spine of goal 1 — every metric in the product is computed from it and nothing is stored — and this
 * is how anybody checks that claim. **It is not USER-06**, the access log: that is a curated set of security events,
 * and reading it is itself logged. This is the whole log.
 *
 * Every filter, the page, the open event and the story are in the address (06-qol §3), so a copied link shows a
 * colleague the same view. Nothing is asked of the server before `canView` says the viewer may see it.
 */

/** Tablet and phone: one column, and a clicked event opens as its own view (as drawn for 375px). */
const SINGLE_COLUMN = '(max-width: 900px)';
const LAST_PAGE = Math.ceil(EVENT_LOG_COUNT_CAP / EVENT_LOG_PAGE_SIZE);

export function EventLogPage(): ReactNode {
  const canView = useCanView('audit_log');
  const router = useRouter();
  const raw = useSearch({ strict: false }) as Readonly<Record<string, unknown>>;
  const search = readEventLogSearch(raw);
  const isSingleColumn = useMediaQuery(SINGLE_COLUMN);
  // The event or the story was opened from this page, so "back" is the browser's Back, not a second history step.
  const [openedHere, setOpenedHere] = useState<{ readonly event: boolean; readonly story: boolean }>({ event: false, story: false });
  if (openedHere.event && search.event === undefined) setOpenedHere((current) => ({ ...current, event: false }));
  if (openedHere.story && search.story === undefined) setOpenedHere((current) => ({ ...current, story: false }));

  const listSearch = listSearchOf(search);
  const filter = filtersOf(listSearch);
  const page = Math.min(search.page ?? 1, LAST_PAGE);
  const isStory = search.story !== undefined && search.storyType !== undefined;

  const facets = useEventLogFacets(canView);
  const log = useEventLog(filter, page, canView && !isStory);
  const story = useEventLogStory(search.storyType, search.story, canView && isStory);
  const rows = log.data?.rows ?? [];
  // Counted from the newest row of these filters' own page, never from the previous filters' rows still on screen.
  const newer = useEventLogNewer(filter, page === 1 ? (rows[0]?.id ?? null) : null, canView && !isStory && log.data !== undefined && !log.isPlaceholderData);
  const showNewer = useShowNewer();
  const exporter = useEventLogExport();

  if (!canView) {
    return <ForbiddenState resource="the event log" contact="Your admin" />;
  }

  /** A change of view replaces the address: Back leaves the log rather than undoing filters. */
  const go = (next: EventLogSearch): void => router.history.replace(eventLogHref(next));
  /** Opening something the person will want Back to close: an event on a phone, a record's story. */
  const open = (next: EventLogSearch): void => router.history.push(eventLogHref(next));

  const openStory = (row: EventLogRow): void => {
    setOpenedHere((current) => ({ ...current, story: true }));
    // On a wide screen the story opens at the event it was opened from; on a phone, at its timeline.
    open({ ...listSearch, ...(search.page ? { page: search.page } : {}), story: row.entityId, storyType: row.entityType, ...(isSingleColumn ? {} : { event: row.id }) });
  };
  const closeStory = (): void => {
    if (openedHere.story) {
      router.history.back();
      return;
    }
    go(listSearch);
  };
  const select = (row: EventLogRow): void => {
    if (isSingleColumn) {
      setOpenedHere((current) => ({ ...current, event: true }));
      open({ ...search, event: row.id });
      return;
    }
    go({ ...search, event: row.id });
  };
  const closeEvent = (): void => {
    if (openedHere.event) {
      router.history.back();
      return;
    }
    const { event: _event, ...rest } = search;
    go(rest);
  };

  if (isStory && search.story && search.storyType) {
    return (
      <div className={styles.page}>
        <EventLogStory
          entityType={search.storyType}
          entityId={search.story}
          story={story.data}
          error={story.error}
          isPending={story.isPending}
          onRetry={() => void story.refetch()}
          selectedId={search.event}
          onSelect={select}
          onAllEvents={closeStory}
          isSingleColumn={isSingleColumn}
          onCloseEvent={closeEvent}
        />
      </div>
    );
  }

  const opened = rows.find((row) => row.id === search.event) ?? null;
  // On a wide screen the newest event is open until another is clicked: one click fewer, nothing empty.
  const selected = opened ?? (isSingleColumn ? null : (rows[0] ?? null));

  if (isSingleColumn && opened) {
    return (
      <div className={styles.page}>
        <EventLogDetail row={opened} onStory={openStory} onBack={closeEvent} />
      </div>
    );
  }

  const total = log.data?.total;
  const isCapped = log.data?.isTotalCapped ?? false;
  const pageCount = Math.min(LAST_PAGE, Math.max(1, Math.ceil((total ?? 0) / EVENT_LOG_PAGE_SIZE)));
  const activeFilters = filterCount(search);
  const hasFailed = log.isError && !log.data;
  const newCount = newer.data?.count ?? 0;
  const actorName = search.actorId ? (rows.find((row) => row.actorId === search.actorId)?.actorName ?? null) : null;

  const emptyState = hasFailed ? (
    <SystemState
      icon={TriangleAlert}
      tone="danger"
      title="Couldn't load the event log"
      body={errorMessage(log.error)}
      actions={
        <Button variant="quiet" onClick={() => void log.refetch()}>
          Try again
        </Button>
      }
      isInset
    />
  ) : page > 1 && total !== undefined && total > 0 ? (
    <EmptyState
      title="This page is past the end"
      body={`The filters match ${formatNumber(total)} ${total === 1 ? 'event' : 'events'}, which fit on fewer pages.`}
      action={
        <Button variant="quiet" onClick={() => go({ ...search, page: undefined, event: undefined })}>
          Go to the first page
        </Button>
      }
      isInset
    />
  ) : activeFilters > 0 ? (
    <FilteredEmptyState filterCount={activeFilters} onClearFilters={() => go({})} isInset />
  ) : (
    <EmptyState
      title="Nothing recorded today yet"
      body="Every change anyone, or the system, makes writes one row here, and every report number is counted from it."
      action={
        <Button variant="quiet" onClick={() => go(withFilters(search, { when: '7' }))}>
          Show the last 7 days
        </Button>
      }
      isInset
    />
  );

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.heading}>
          <div className={styles.titleRow}>
            <h1 className={styles.title}>Event log</h1>
            {facets.data && <span className={styles.total}>{formatNumber(facets.data.total)}</span>}
          </div>
          <p className={styles.lede}>Every change anyone, or the system, made, in the order it happened. Every report number is counted from this list. Nothing here can be edited.</p>
        </div>
        <Button variant="outline" icon={Download} isPending={exporter.isPreparing} onClick={() => exporter.run(filter)}>
          Export these rows
        </Button>
      </header>

      <EventLogFilters search={search} facets={facets.data} total={total} isTotalCapped={isCapped} actorName={actorName} onChange={(next) => go(next)} />

      {page === 1 && newCount > 0 && (
        <div className={styles.newer}>
          <button type="button" className={styles.newerPill} onClick={() => void showNewer()}>
            {newCount > EVENT_LOG_NEWER_CAP ? `${EVENT_LOG_NEWER_CAP}+` : formatNumber(newCount)} new · Show
          </button>
        </div>
      )}

      <div className={styles.layout}>
        <div className={styles.tableArea} aria-busy={log.isFetching || undefined}>
          <EventLogTable rows={rows} selectedId={selected?.id ?? null} onSelect={select} isSelectOnMove={!isSingleColumn} isFirstLoad={log.isPending} emptyState={emptyState} />
          {rows.length > 0 && total !== undefined && (
            <div className={styles.pager}>
              <Pager page={page} pageCount={pageCount} pageSize={EVENT_LOG_PAGE_SIZE} rowCount={rows.length} total={total} onPage={(next) => go({ ...search, page: next > 1 ? next : undefined, event: undefined })} />
              {isCapped && page === pageCount && <p className={styles.capNote}>That&apos;s as far as the pages go. Narrow the dates or pick a type to see older events.</p>}
              {isCapped && page !== pageCount && <p className={styles.capNote}>More than {formatNumber(EVENT_LOG_COUNT_CAP)} events match: the pages reach the newest {formatNumber(EVENT_LOG_COUNT_CAP)}.</p>}
            </div>
          )}
        </div>

        {!isSingleColumn && (
          <div className={styles.detailArea}>
            {selected ? <EventLogDetail row={selected} onStory={openStory} /> : <p className={styles.pick}>{log.isPending ? '' : 'Click an event to see what changed.'}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
